import { of } from "rxjs";
import { catchError, filter, map, mergeMap } from "rxjs/operators";
import { StateObservable } from "redux-observable";

import { Effect } from "../../../../../redux/types/effect.type";
import { EffectActionsObservable } from "../../../../../redux/types/effect-actions-observable.type";
import { isActionType } from "../../../../../redux/action.functions";
import { RootState } from "../../../../../redux/store";
import { addMessage } from "../../../../../redux/actions/messagesActions";

import {
  createGridFromTemplate,
  moveGridLine,
  scaleToCanvas,
} from "../../../features/flyer-template/types/grid/grid.functions";
import { FlyerTemplateMutation } from "../../../features/flyer-template/types/flyer-template/flyer-template-mutation.interface";
import { createCanvas } from "../../../features/flyer-template/types/canvas/canvas.functions";
import { mapFlyerTemplateResponseToTemplate } from "../../../features/flyer-template/types/flyer-template-response/flyer-template-response.functions";

import * as templateService from "../services/flyer-template.service";

import * as actions from "./flyer-template-creator.actions";
import * as store from "./flyer-template-creator.store";
import { FlyerTemplateCreatorActions } from "./flyer-template-creator.actions";

const getTemplateAndCanvas = (state$: StateObservable<RootState>) => () => ({
  template: store.getTemplate(state$.value),
  canvas: store.getCanvas(state$.value),
});

const applyTemplateMutation = (mutation: FlyerTemplateMutation) =>
  of(actions.setGrid(mutation.grid), actions.setTemplate(mutation.template));

const createGrid: Effect = (action$: EffectActionsObservable, state$: StateObservable<RootState>) =>
  action$.pipe(
    filter(isActionType(FlyerTemplateCreatorActions.CreateGrid)),
    map(() => ({
      template: store.getTemplate(state$.value),
      canvas: store.getCanvas(state$.value),
    })),
    map(({ template, canvas }) => actions.setGrid(createGridFromTemplate(template, canvas)))
  );

const resetTemplate: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(FlyerTemplateCreatorActions.Reset)),
    map(() => actions.createGrid())
  );

const moveGridLineEffect: Effect = (
  action$: EffectActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    filter(isActionType(FlyerTemplateCreatorActions.MoveGridLine)),
    map((action) => ({
      line: action.payload.line,
      position: action.payload.position,
      grid: store.getGrid(state$.value),
    })),
    map(({ line, position, grid }) => actions.setGrid(moveGridLine(line, position, grid)))
  );

const createPosition: Effect = (
  action$: EffectActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    filter(isActionType(FlyerTemplateCreatorActions.CreatePosition)),
    map((action) => ({
      selection: action.payload,
      template: store.getTemplate(state$.value),
    })),
    map(({ selection, template }) =>
      actions.setTemplate(templateService.addPosition(selection, template!))
    )
  );

const addRow: Effect = (action$: EffectActionsObservable, state$: StateObservable<RootState>) =>
  action$.pipe(
    filter(isActionType(FlyerTemplateCreatorActions.AddRow)),
    map(getTemplateAndCanvas(state$)),
    map(({ template, canvas }) => templateService.addRowToTemplate(template!, canvas!)),
    mergeMap(applyTemplateMutation)
  );

const removeRow: Effect = (action$: EffectActionsObservable, state$: StateObservable<RootState>) =>
  action$.pipe(
    filter(isActionType(FlyerTemplateCreatorActions.RemoveRow)),
    map(getTemplateAndCanvas(state$)),
    map(({ template, canvas }) => templateService.removeRowFromTemplate(template!, canvas!)),
    mergeMap(applyTemplateMutation)
  );

const setRows: Effect = (action$: EffectActionsObservable, state$: StateObservable<RootState>) =>
  action$.pipe(
    filter(isActionType(FlyerTemplateCreatorActions.SetRows)),
    map((action) => ({ rows: action.payload, ...getTemplateAndCanvas(state$)() })),
    map(({ rows, template, canvas }) => templateService.setTemplateRows(rows, template!, canvas!)),
    mergeMap(applyTemplateMutation)
  );

const addColumn: Effect = (action$: EffectActionsObservable, state$: StateObservable<RootState>) =>
  action$.pipe(
    filter(isActionType(FlyerTemplateCreatorActions.AddColumn)),
    map(getTemplateAndCanvas(state$)),
    map(({ template, canvas }) => templateService.addColumnToTemplate(template!, canvas!)),
    mergeMap(applyTemplateMutation)
  );

const removeColumn: Effect = (
  action$: EffectActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    filter(isActionType(FlyerTemplateCreatorActions.RemoveColumn)),
    map(getTemplateAndCanvas(state$)),
    map(({ template, canvas }) => templateService.removeColumnFromTemplate(template!, canvas!)),
    mergeMap(applyTemplateMutation)
  );

const setColumns: Effect = (action$: EffectActionsObservable, state$: StateObservable<RootState>) =>
  action$.pipe(
    filter(isActionType(FlyerTemplateCreatorActions.SetColumns)),
    map((action) => ({ columns: action.payload, ...getTemplateAndCanvas(state$)() })),
    map(({ columns, template, canvas }) =>
      templateService.setTemplateColumns(columns, template!, canvas!)
    ),
    mergeMap(applyTemplateMutation)
  );

const updateCanvas: Effect = (
  action$: EffectActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    filter(isActionType(FlyerTemplateCreatorActions.UpdateCanvas)),
    map((action) => ({
      template: store.getTemplate(state$.value),
      grid: store.getGrid(state$.value),
      currentCanvas: store.getCanvas(state$.value),
      newCanvas: action.payload,
    })),
    map(({ template, grid, newCanvas, currentCanvas }) => ({
      scaledGrid: scaleToCanvas(template!, grid, newCanvas, currentCanvas!),
      newCanvas,
    })),
    mergeMap(({ scaledGrid, newCanvas }) =>
      of(actions.setCanvas(newCanvas), actions.setGrid(scaledGrid))
    )
  );

const removePosition: Effect = (
  action$: EffectActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    filter(isActionType(FlyerTemplateCreatorActions.RemovePosition)),
    map((action) => ({
      positionId: action.payload,
      template: store.getTemplate(state$.value),
    })),
    map(({ positionId, template }) =>
      actions.setTemplate(templateService.removePosition(positionId, template!))
    )
  );

const movePosition: Effect = (
  action$: EffectActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    filter(isActionType(FlyerTemplateCreatorActions.MovePosition)),
    map((action) => ({
      ...action.payload,
      template: store.getTemplate(state$.value),
    })),
    map(({ positionIndex, rows, columns, template }) =>
      actions.setTemplate(templateService.movePosition(positionIndex, rows, columns, template!))
    )
  );

const setAspectRatio: Effect = (
  action$: EffectActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    filter(isActionType(FlyerTemplateCreatorActions.SetAspectRatio)),
    map((action) => ({
      ...action.payload,
      template: store.getTemplate(state$.value),
      canvas: store.getCanvas(state$.value),
    })),
    mergeMap(({ ratio, screenWidth, screenHeight, template, canvas }) =>
      of(
        actions.updateCanvas(createCanvas(screenWidth, screenHeight, ratio)),
        actions.setTemplate(templateService.setAspectRatio(ratio, template))
      )
    )
  );

const saveTemplate: Effect = (
  action$: EffectActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    filter(isActionType(FlyerTemplateCreatorActions.Save)),
    map(() => store.getTemplate(state$.value)),
    mergeMap((template) =>
      templateService.saveTemplate(template!).pipe(
        map((template) => actions.saveSuccess(mapFlyerTemplateResponseToTemplate(template))),
        catchError((error) => of(actions.saveFailure(error)))
      )
    )
  );

const saveTemplateSuccess: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(FlyerTemplateCreatorActions.SaveSuccess)),
    map(() => addMessage({ text: "Successfully saved template.", severity: "success" }))
  );

const savePositionDetails: Effect = (
  action$: EffectActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    filter(isActionType(FlyerTemplateCreatorActions.SavePositionDetails)),
    map((action) => ({
      form: action.payload,
      template: store.getTemplate(state$.value),
    })),
    map(({ form, template }) =>
      actions.savePositionDetailsSuccess(templateService.savePositionDetails(form, template!))
    )
  );

const savePositionDetailsSuccess: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(FlyerTemplateCreatorActions.SavePositionDetailsSuccess)),
    map((action) => action.payload),
    map((template) => actions.setTemplate(template))
  );

export const FlyerTemplateCreatorEffects = [
  createGrid,
  moveGridLineEffect,
  updateCanvas,
  addRow,
  removeRow,
  setRows,
  addColumn,
  removeColumn,
  setColumns,
  createPosition,
  removePosition,
  resetTemplate,
  movePosition,
  setAspectRatio,
  savePositionDetails,
  savePositionDetailsSuccess,
  saveTemplate,
  saveTemplateSuccess,
];
